import { JwtHelperService } from '@auth0/angular-jwt';
import { AppSettings } from '../../app.settings';
import { TabStorageService } from '../tabstorage/tabstorage.service';
import { CredentialContext, CredentialRefresh } from '../session/credential-transition';
const apiUnauthorizedState = {
  redirectInProgress: false,
};
const apiRefresh = new CredentialRefresh(TabStorageService.credentials);

const jwtHelper = new JwtHelperService();
const ABSOLUTE_SESSION_EXPIRATION_CLAIM = 'sessionAbsoluteExp';

const isAbsoluteSessionExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }

  try {
    const decodedToken = jwtHelper.decodeToken(token);
    const absoluteExpiration = Number(decodedToken?.[ABSOLUTE_SESSION_EXPIRATION_CLAIM]);
    return !Number.isFinite(absoluteExpiration) || absoluteExpiration <= Math.floor(Date.now() / 1000);
  } catch (_) {
    return true;
  }
};

const hasRequiredExpirationClaim = (token: string | null): boolean => {
  if (!token) {
    return false;
  }

  const decodedToken = jwtHelper.decodeToken(token);
  return Boolean(decodedToken && Number.isFinite(Number(decodedToken.exp)));
};

const hasValidStoredJwtToken = (): boolean => {
  const token = TabStorageService.getStorage(TabStorageService.JWT_TOKEN);
  if (!token) {
    return false;
  }

  try {
    return hasRequiredExpirationClaim(token) && !isAbsoluteSessionExpired(token) && !jwtHelper.isTokenExpired(token);
  } catch (_) {
    return false;
  }
};

const getApiRequestUrl = (response: any): string => {
  return response?.request?.url || response?.config?.url || response?.url || '';
};

const isWallitApiRequestUrl = (requestUrl: string): boolean => {
  if (!requestUrl || !/^https?:\/\//i.test(requestUrl)) {
    return true;
  }

  try {
    const requestHost = new URL(requestUrl).host;
    const endpointHost = new URL(AppSettings.getEndpoint()).host;
    return requestHost === endpointHost || requestHost.endsWith('.api.wallit.app');
  } catch (_) {
    return requestUrl.startsWith(AppSettings.getEndpoint());
  }
};

const shouldAttemptApiRefresh = (response: any): boolean => {
  const requestUrl = getApiRequestUrl(response);
  return Boolean(
    response?.status === 401 &&
    isWallitApiRequestUrl(requestUrl) &&
    TabStorageService.getStorage(TabStorageService.JWT_TOKEN) &&
    hasRequiredExpirationClaim(TabStorageService.getStorage(TabStorageService.JWT_TOKEN)) &&
    TabStorageService.getStorage(TabStorageService.REFRESH_TOKEN) &&
    !isAbsoluteSessionExpired(TabStorageService.getStorage(TabStorageService.JWT_TOKEN)) &&
    !requestUrl.includes('/auth') &&
    !requestUrl.includes('globalconfig.api') &&
    !requestUrl.includes('infra-mgmt.api') &&
    !requestUrl.includes('be-playground.api') &&
    !requestUrl.includes(AppSettings.getIntegrationEndpoint())
  );
};

const refreshStoredJwtToken = (context?: CredentialContext): Promise<any> =>
  apiRefresh.run(() => !context || context.current(), context);

// A late failure may only clear the credentials actually used by that still-current request.
const canClearRequestCredentials = (context: CredentialContext | null, response: any): boolean => {
  if (!context) { return true; }
  const sent = response?.request?.headers?.get('Authorization') || `Bearer ${context.token}`;
  return context.current() && sent === `Bearer ${TabStorageService.getStorage(TabStorageService.JWT_TOKEN)}`;
};

const withCurrentJwtToken = (request: any): any => {
  const token = TabStorageService.getStorage(TabStorageService.JWT_TOKEN);
  return token && request?.clone
    ? request.clone({headers: request.headers.set('Authorization', `Bearer ${token}`)})
    : request;
};

const repeatApiRequest = (response: any): any => {
  if (!response?.repeatRequest) {
    return null;
  }

  const requestWithFreshToken = withCurrentJwtToken(response.request);
  return response.request
    ? response.repeatRequest(requestWithFreshToken)
    : response.repeatRequest();
};

const forceLogout = (): void => {
  if (apiUnauthorizedState.redirectInProgress) {
    return;
  }
  apiUnauthorizedState.redirectInProgress = true;
  TabStorageService.clearCurrentTabSession();
  window.location.assign('/login/false');
};


export { shouldAttemptApiRefresh, refreshStoredJwtToken, canClearRequestCredentials, repeatApiRequest, forceLogout, hasValidStoredJwtToken };
